import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AppBar, Toolbar, Typography, Box, IconButton, Avatar, Menu, MenuItem,
  Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Button, TextField, Chip, Select, FormControl, InputLabel,
  Dialog, DialogTitle, DialogContent, DialogActions, CircularProgress,
  Alert, Checkbox, FormControlLabel,
} from '@mui/material';
import { ArrowBack, ExitToApp, LocalHospital, Add, Delete, BeachAccess } from '@mui/icons-material';
import { authService, leaveService } from '../services/api';

const LEAVE_TYPES = ['Casual', 'Sick', 'Unpaid'];
const LEAVE_COLOR = { Casual: 'primary', Sick: 'warning', Unpaid: 'error' };
const STATUS_COLOR = { Pending: 'warning', Approved: 'success', Rejected: 'error' };

const fmt = d => d ? new Date(d).toLocaleDateString('en-IN') : '—';
const monthName = m => new Date(2000, m - 1, 1).toLocaleString('default', { month: 'long' });

const today = new Date().toISOString().split('T')[0];

const emptyForm = () => ({
  fromDate: today,
  toDate: today,
  leaveType: 'Casual',
  reason: '',
  isHalfDay: false,
});

const totalDays = row => {
  if (row.isHalfDay) return 0.5;
  const from = new Date(row.fromDate);
  const to   = new Date(row.toDate);
  return Math.round((to - from) / 86400000) + 1;
};

export default function MyLeavePage() {
  const navigate = useNavigate();
  const user = authService.getCurrentUser();
  const [anchorEl, setAnchorEl] = useState(null);

  const now = new Date();
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year,  setYear]  = useState(now.getFullYear());

  const [leaves,  setLeaves]  = useState([]);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState('');

  const [dialogOpen, setDialogOpen] = useState(false);
  const [form,       setForm]       = useState(emptyForm());
  const [saving,     setSaving]     = useState(false);
  const [cancelId,   setCancelId]   = useState(null);

  const loadLeaves = useCallback(async () => {
    setLoading(true);
    try {
      const res = await leaveService.getMy({ month, year });
      setLeaves(res.data);
    } catch {
      setError('Failed to load leave requests.');
    } finally {
      setLoading(false);
    }
  }, [month, year]);

  useEffect(() => { loadLeaves(); }, [loadLeaves]);

  const handleField = f => e => setForm(prev => ({ ...prev, [f]: e.target.value }));

  const handleFromDateChange = e => {
    const val = e.target.value;
    setForm(prev => ({ ...prev, fromDate: val, toDate: prev.isHalfDay ? val : prev.toDate }));
  };

  const handleHalfDayChange = e => {
    const checked = e.target.checked;
    setForm(prev => ({ ...prev, isHalfDay: checked, toDate: checked ? prev.fromDate : prev.toDate }));
  };

  const handleSubmit = async () => {
    if (!form.fromDate || !form.toDate) return;
    if (form.toDate < form.fromDate) {
      setError('To Date cannot be before From Date.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      await leaveService.create({
        fromDate:  form.fromDate,
        toDate:    form.isHalfDay ? form.fromDate : form.toDate,
        leaveType: form.leaveType,
        reason:    form.reason,
        isHalfDay: form.isHalfDay,
      });
      setDialogOpen(false);
      setForm(emptyForm());
      await loadLeaves();
    } catch {
      setError('Failed to submit leave request.');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = async () => {
    try {
      await leaveService.delete(cancelId);
      setCancelId(null);
      await loadLeaves();
    } catch {
      setError('Failed to cancel leave request.');
    }
  };

  const pendingCount  = leaves.filter(l => l.status === 'Pending').length;
  const approvedCount = leaves.filter(l => l.status === 'Approved').length;
  const rejectedCount = leaves.filter(l => l.status === 'Rejected').length;

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      <AppBar position="static" className="medical-gradient">
        <Toolbar>
          <IconButton edge="start" color="inherit" onClick={() => navigate('/dashboard')} sx={{ mr: 2 }}>
            <ArrowBack />
          </IconButton>
          <BeachAccess sx={{ fontSize: 32, mr: 1.5 }} />
          <Typography variant="h5" sx={{ flexGrow: 1, fontWeight: 600 }}>
            My Leave Requests
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="body1">{user?.firstName} {user?.lastName}</Typography>
            <IconButton size="large" onClick={e => setAnchorEl(e.currentTarget)} color="inherit">
              <Avatar sx={{ bgcolor: '#1565c0' }}>{user?.firstName?.charAt(0)}</Avatar>
            </IconButton>
            <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
              <MenuItem onClick={() => { authService.logout(); navigate('/'); }}>
                <ExitToApp sx={{ mr: 1 }} /> Logout
              </MenuItem>
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>

      <Box sx={{ p: 3 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>
        )}

        {/* Controls */}
        <Paper elevation={2} sx={{ p: 2, mb: 3, borderRadius: 2 }}>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            <FormControl size="small" sx={{ minWidth: 130 }}>
              <InputLabel>Month</InputLabel>
              <Select value={month} label="Month" onChange={e => setMonth(e.target.value)}>
                {Array.from({ length: 12 }, (_, i) => (
                  <MenuItem key={i + 1} value={i + 1}>{monthName(i + 1)}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 100 }}>
              <InputLabel>Year</InputLabel>
              <Select value={year} label="Year" onChange={e => setYear(e.target.value)}>
                {[now.getFullYear() - 1, now.getFullYear(), now.getFullYear() + 1].map(y => (
                  <MenuItem key={y} value={y}>{y}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => { setForm(emptyForm()); setDialogOpen(true); }}
              sx={{ ml: 'auto' }}
            >
              Request Leave
            </Button>
          </Box>
        </Paper>

        {/* Summary chips */}
        <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
          <Chip label={`Pending: ${pendingCount}`}  color="warning" variant="outlined" size="small" />
          <Chip label={`Approved: ${approvedCount}`} color="success" variant="outlined" size="small" />
          <Chip label={`Rejected: ${rejectedCount}`} color="error"   variant="outlined" size="small" />
          <Chip label={`Total: ${leaves.length}`}    variant="outlined" size="small" />
        </Box>

        {loading ? (
          <Box sx={{ textAlign: 'center', py: 6 }}><CircularProgress /></Box>
        ) : (
          <Paper elevation={3} sx={{ borderRadius: 2 }}>
            <TableContainer>
              <Table size="small">
                <TableHead sx={{ bgcolor: '#1565c0' }}>
                  <TableRow>
                    <TableCell sx={{ color: '#fff', fontWeight: 700 }}>#</TableCell>
                    <TableCell sx={{ color: '#fff', fontWeight: 700 }}>Type</TableCell>
                    <TableCell sx={{ color: '#fff', fontWeight: 700 }}>From</TableCell>
                    <TableCell sx={{ color: '#fff', fontWeight: 700 }}>To</TableCell>
                    <TableCell sx={{ color: '#fff', fontWeight: 700 }} align="center">Days</TableCell>
                    <TableCell sx={{ color: '#fff', fontWeight: 700 }}>Reason</TableCell>
                    <TableCell sx={{ color: '#fff', fontWeight: 700 }}>Status</TableCell>
                    <TableCell sx={{ color: '#fff', fontWeight: 700 }} align="center">Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {leaves.map((row, idx) => (
                    <TableRow key={row.id} hover>
                      <TableCell>{idx + 1}</TableCell>
                      <TableCell>
                        <Chip label={row.leaveType} color={LEAVE_COLOR[row.leaveType]} size="small" />
                        {row.isHalfDay && <Chip label="½ Day" size="small" variant="outlined" sx={{ ml: 0.5 }} />}
                      </TableCell>
                      <TableCell>{fmt(row.fromDate)}</TableCell>
                      <TableCell>{row.isHalfDay ? '—' : fmt(row.toDate)}</TableCell>
                      <TableCell align="center">{totalDays(row)}</TableCell>
                      <TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {row.reason || '—'}
                      </TableCell>
                      <TableCell>
                        <Chip label={row.status} color={STATUS_COLOR[row.status]} size="small" />
                      </TableCell>
                      <TableCell align="center">
                        {row.status === 'Pending' && (
                          <IconButton size="small" color="error" title="Cancel request" onClick={() => setCancelId(row.id)}>
                            <Delete fontSize="small" />
                          </IconButton>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                  {leaves.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={8} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                        No leave requests for {monthName(month)} {year}.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        )}
      </Box>

      {/* Request Leave Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Request Leave</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 3, overflow: 'visible' }}>
          <FormControlLabel
            control={<Checkbox checked={form.isHalfDay} onChange={handleHalfDayChange} />}
            label="Half Day Leave"
          />

          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              label="Date"
              type="date"
              size="small"
              fullWidth
              value={form.fromDate}
              onChange={handleFromDateChange}
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: today }}
            />
            {!form.isHalfDay && (
              <TextField
                label="To Date"
                type="date"
                size="small"
                fullWidth
                value={form.toDate}
                onChange={handleField('toDate')}
                InputLabelProps={{ shrink: true }}
                inputProps={{ min: form.fromDate }}
              />
            )}
          </Box>

          <FormControl size="small" fullWidth>
            <InputLabel>Leave Type</InputLabel>
            <Select value={form.leaveType} label="Leave Type" onChange={handleField('leaveType')}>
              {LEAVE_TYPES.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
            </Select>
          </FormControl>

          <TextField
            label="Reason (optional)"
            size="small"
            multiline
            rows={3}
            value={form.reason}
            onChange={handleField('reason')}
          />

          <Alert severity="info" sx={{ mt: 0.5 }}>
            Your request will be sent to the admin for approval.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={saving}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
          >
            Submit Request
          </Button>
        </DialogActions>
      </Dialog>

      {/* Cancel Confirm Dialog */}
      <Dialog open={!!cancelId} onClose={() => setCancelId(null)}>
        <DialogTitle>Cancel Leave Request</DialogTitle>
        <DialogContent>Are you sure you want to cancel this leave request?</DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelId(null)}>No</Button>
          <Button variant="contained" color="error" onClick={handleCancel}>Yes, Cancel</Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
